/** Сортировка дерева по столбцу (клик по заголовку): ключи, адрес, порядок соседей. Без React.
 *
 *  Сортируются только соседи одного уровня (уже загруженные страницы узла); структура дерева не
 *  меняется. Значения — те же числа, что показаны в ячейках (`compact.treeCellNumbers`), новых
 *  расчётов нет. Пусто / «—» / нет сравнения (null) — всегда в конце, при любом направлении;
 *  ничьи — в порядке ответа бэкенда. Справочные группы (`flags.reference`, WB `spp`) в P&L не
 *  входят — их не сортируем, они идут после остальных (как блок «Справочно» под P&L).
 *
 *  Адрес: `tsort=<ключ>` (неизвестный — сортировки нет, как у прочих перечислимых параметров),
 *  `tdir=asc` — по возрастанию (иное — по убыванию). Ключи Δ без сравнения не действуют. */
import type { TreeRow } from "@/api/ozonAnalytics";
import type { MetricNumbers, TreeCellNumbers, TreeColumn, TreeMetricId } from "./compact";

type Role = TreeColumn["role"];
export type SortVariant = "rub" | "qty" | "pct" | "pp";

interface KeyDef {
  metric: TreeMetricId;
  role: Role;
  variant: SortVariant;
  pick: keyof MetricNumbers;
}

/** Ключи — в порядке колонок и вариантов меню. `cost` — «Расходы» (удержания + себестоимость),
 *  `margin` — «Маржа» (с 22.10.2026; ключи прежние — адреса не ломаются); «—» (нет себестоимости) —
 *  в конце. Прежние ключи `cogs…`/`net…` (колонки «Закупочная цена» / «Чистая маржа») сняты —
 *  в адресе они теперь неизвестны: сортировки нет. */
const KEYS = {
  rev: { metric: "rev", role: "value", variant: "rub", pick: "value" },
  rev_qty: { metric: "rev", role: "value", variant: "qty", pick: "sub" },
  rev_d: { metric: "rev", role: "delta", variant: "rub", pick: "dAbs" },
  rev_d_pct: { metric: "rev", role: "delta", variant: "pct", pick: "dRel" },
  cost: { metric: "cost", role: "value", variant: "rub", pick: "value" },
  cost_pct: { metric: "cost", role: "value", variant: "pct", pick: "sub" },
  cost_pp: { metric: "cost", role: "pp", variant: "pp", pick: "pp" },
  cost_d: { metric: "cost", role: "delta", variant: "rub", pick: "dAbs" },
  cost_d_pct: { metric: "cost", role: "delta", variant: "pct", pick: "dRel" },
  margin: { metric: "margin", role: "value", variant: "rub", pick: "value" },
  margin_pct: { metric: "margin", role: "value", variant: "pct", pick: "sub" },
  margin_pp: { metric: "margin", role: "pp", variant: "pp", pick: "pp" },
  margin_d: { metric: "margin", role: "delta", variant: "rub", pick: "dAbs" },
  margin_d_pct: { metric: "margin", role: "delta", variant: "pct", pick: "dRel" },
} as const satisfies Record<string, KeyDef>;

export type ColSortKey = keyof typeof KEYS;
export const COL_SORT_KEYS = Object.keys(KEYS) as ColSortKey[];

export type SortDir = "desc" | "asc";

export interface ColSort {
  key: ColSortKey;
  dir: SortDir;
}

export const VARIANT_LABEL: Record<SortVariant, string> = {
  rub: "₽",
  qty: "шт",
  pct: "%",
  pp: "п.п.",
};

export function variantOf(key: ColSortKey): SortVariant {
  return KEYS[key].variant;
}

/** Ключ — по Δ (п.п., ₽, %): без периода сравнения недоступен. */
export function isDeltaKey(key: ColSortKey): boolean {
  return KEYS[key].role !== "value";
}

/** Варианты сортировки колонки — в порядке меню. */
export function columnSortKeys(col: Pick<TreeColumn, "metric" | "role">): ColSortKey[] {
  return COL_SORT_KEYS.filter((k) => KEYS[k].metric === col.metric && KEYS[k].role === col.role);
}

/** Прежние ключи колонки «Чистая маржа» (до 22.10.2026) → ключи «Маржи»: та же величина, адрес
 *  переписывается один раз (`TreeTab`). Ключей «Закупочной цены» (`cogs…`) соответствия нет —
 *  колонки больше нет, такой адрес — ошибка с «Сбросить». */
const LEGACY_KEYS: Record<string, ColSortKey> = {
  net: "margin",
  net_pct: "margin_pct",
  net_pp: "margin_pp",
  net_d: "margin_d",
  net_d_pct: "margin_d_pct",
};

export function legacyColSortKey(raw: string | null): ColSortKey | null {
  return raw !== null && Object.prototype.hasOwnProperty.call(LEGACY_KEYS, raw) ? LEGACY_KEYS[raw]! : null;
}

/** Адрес → сортировка по столбцу; неизвестный ключ — сортировки нет (`colSortBad` — ошибка). */
export function parseColSort(key: string | null, dir: string | null): ColSort | null {
  if (key === null || !(COL_SORT_KEYS as string[]).includes(key)) return null;
  return { key: key as ColSortKey, dir: dir === "asc" ? "asc" : "desc" };
}

export function colSortPatch(s: ColSort | null): Record<string, string | null> {
  return { tsort: s?.key ?? null, tdir: s?.dir === "asc" ? "asc" : null };
}

/** Выбор варианта в меню: новый — по убыванию, тот же ещё раз — обратное направление. */
export function nextColSort(cur: ColSort | null, key: ColSortKey): ColSort {
  if (cur?.key === key) return { key, dir: cur.dir === "desc" ? "asc" : "desc" };
  return { key, dir: "desc" };
}

/** Действующая сортировка: ключ Δ без сравнения не действует (null). */
export function effectiveColSort(s: ColSort | null, hasCmp: boolean): ColSort | null {
  return s && (hasCmp || !isDeltaKey(s.key)) ? s : null;
}

export function sortValue(n: TreeCellNumbers, key: ColSortKey): number | null {
  const d = KEYS[key];
  return n[d.metric][d.pick];
}

/** Соседи одного уровня по столбцу; `numbers` — числа ячеек строки (`treeCellNumbers`). */
export function sortTreeRows(
  rows: readonly TreeRow[],
  s: ColSort,
  numbers: (r: TreeRow) => TreeCellNumbers,
): TreeRow[] {
  const sign = s.dir === "desc" ? -1 : 1;
  const ranked = rows
    .filter((r) => r.flags.reference !== true)
    .map((r, i) => ({ r, i, v: sortValue(numbers(r), s.key) }));
  ranked.sort((a, b) => {
    if (a.v === null || b.v === null) {
      if (a.v === b.v) return a.i - b.i;
      return a.v === null ? 1 : -1;
    }
    return a.v !== b.v ? sign * (a.v - b.v) : a.i - b.i;
  });
  return [...ranked.map((x) => x.r), ...rows.filter((r) => r.flags.reference === true)];
}
